import * as React from 'react';
import { buttonLinkStyles, ShippingLineIcon, ShippingLineLabel, ShippingLineText, textStyles } from './helpers/styles';
import { faMapMarkedAlt } from '@fortawesome/pro-duotone-svg-icons/faMapMarkedAlt';
import { FormattedMessage } from 'react-intl';
import { getCatalog } from '@/redux/modules/catalog';
import { getItemDetail } from '@/redux/modules/itemDetail';
import { getListingAgent } from '@/redux/modules/listingAgent';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
};

const JASPER52_ID = 5584;

const ShipsFromLine = ({ catalogId, itemId }: Props) => {
    const { address: catalogAddress, sellerId } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { agentId } = useSelector((state) => getItemDetail(state, itemId));
    const listingAgent = useSelector((state) => getListingAgent(state, agentId));

    const { city, country, fullCountryName, state } = catalogAddress;
    const isJasper52 = sellerId === JASPER52_ID;

    const listingAgentInUS =
        listingAgent.country && ['US', 'UNITED STATES'].includes(listingAgent.country.toUpperCase());

    // If a catalog address is invalid, hide line
    if (!isJasper52 && !country) {
        return null;
    }

    return (
        <>
            <ShippingLineIcon icon={faMapMarkedAlt} />
            <ShippingLineLabel>
                <FormattedMessage id="shippingInfo.shipsFromLabel" />
            </ShippingLineLabel>
            <StyledShippingLineText>
                {isJasper52 ? (
                    <FormattedMessage
                        id="shippingInfo.shipsFromJasper52"
                        values={{
                            agentId,
                            location: listingAgentInUS ? listingAgent.state : listingAgent.country,
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id={state ? 'shippingInfo.shippingLocation' : 'shippingInfo.shippingLocationNoState'}
                        values={{
                            city,
                            country: fullCountryName,
                            ...(state ? { state: state.toUpperCase() } : {}),
                        }}
                    />
                )}
            </StyledShippingLineText>
            {isJasper52 && (
                <>
                    <FlexRowSpacer />
                    <StyledLink rel="noopener" route target="_blank" to={`/seller/${agentId}/`}>
                        <FormattedMessage id="shippingInfo.sellerLink" />
                    </StyledLink>
                </>
            )}
        </>
    );
};

export default React.memo<Props>(ShipsFromLine);

const FlexRowSpacer = styled.div`
    flex-basis: 100%;
`;

const StyledShippingLineText = styled(ShippingLineText)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @media (min-width: 768px) and (max-width: 951px) {
        margin-left: 45px;
    }
    @media (min-width: 1065px) {
        margin-left: 0;
    }

    @media (max-width: 404px) {
        margin-left: 45px;
    }
`;

const StyledLink = styled(Link)`
    ${textStyles}
    ${buttonLinkStyles}
    margin-left: 45px;
`;
